// @ts-ignore
import React, { useEffect } from "react";

import { RootComponentInstance } from "@uniformdev/canvas";
import { useIntl } from "react-intl";

import FeatureGates from "@atlaskit/feature-gate-js-client";
import {
    getSSRAutoScript,
    getThemeHtmlAttrs,
    getThemeStyles,
    setGlobalTheme
} from "@atlaskit/tokens";
import type AnalyticsWebClient from "@atlassiansox/analytics-web-client";
import { AvailableProductsResponse } from "@atlassiansox/asc-stargate";

import type {
    Template,
    TemplateCollection
} from "@/content/templates/template.types";
import {
    ServerSideRequestStatus,
    UserProfile,
    UserProfileResponse
} from "@/libraries/bxp-web-stargate-client/src";

import { TemplateCategory } from "./components/confluence/templates/template-categories-search";
import { CustomerStory } from "./content/customerStories/customerStories.types";
import { AppPricingData } from "./contentful";
import { Layout, PageProps } from "./layout";
import { AgileTutorial } from "./templates/agile-hub/tutorial-types";
import { AgileArticle, AgileOverview } from "./templates/agile-hub/types";
import {
    getAnchorContents,
    DEFAULT_PERSONA
} from "./utils/anchor-queries/get-anchor-contents";
import { getTemplateMetadata } from "./utils/content-queries/get-content-metadata";
import { getPricingContent } from "./utils/pricing-queries/get-pricing-content";
import { UserLocale } from "./utils/useGeoCurrency";
import { parseRoute } from "./wac-routes";

import type { GrowthUnifiedProfile } from "./libraries/growth-unified-profile-client";
import type { PreloadedQuery } from "react-relay";
import type { OperationType } from "relay-runtime";

export type ParcelManifestModuleType = "global" | "esmodule";
export type ParcelManifestLoadType = "sync" | "async" | "defer";

export type ParcelEntryManifest = {
    [key in ParcelManifestModuleType]: {
        [key in ParcelManifestLoadType]: string[];
    };
};

type PageWrapperProps = {
    analyticsWebClient?: AnalyticsWebClient;
    location: Location;
    manifest?: ParcelEntryManifest;
    inlineCss?: string;
    pricing?: AppPricingData;
    availableProducts?: AvailableProductsResponse;
    userProfile?: UserProfile;
    userProfileStatus?: ServerSideRequestStatus;
    userContext?: UserProfileResponse;
    userContextStatus?: ServerSideRequestStatus;
    loggedOutContext?: GrowthUnifiedProfile;
    loggedOutContextStatus?: ServerSideRequestStatus;
    countryCode?: UserLocale;
    pureClientMode?: boolean;
    templates?: Template[];
    agileArticle?: AgileArticle;
    agileOverview?: AgileOverview;
    tutorial?: AgileTutorial;
    customerStory?: CustomerStory;
    partnerTemplates?: Template[];
    collections?: TemplateCollection[];
    categories?: TemplateCategory[];
    themeStyles?: Awaited<ReturnType<typeof getThemeStyles>>;
    anchorContents?: Awaited<ReturnType<typeof getAnchorContents>>;
    pricingContent?: Awaited<ReturnType<typeof getPricingContent>>;
    persona?: string;
    teamType?: string;
    composition?: RootComponentInstance;
    isUniformPlayground?: boolean;
    keyword?: string;
    mlTemplateIds?: string[];
    anchor?: string;
    subAnchor?: string;
    referrer?: string;
    track?: string;
    queryRef?: PreloadedQuery<OperationType>;
    hasCloudSessionTokenCookie?: boolean;
    children: React.ComponentType<PageProps>;
};

const SUPPORTED_LOCALES = [
    { hrefLang: "ja", langRoute: "ja" },
    { hrefLang: "fr", langRoute: "fr" },
    { hrefLang: "de", langRoute: "de" },
    { hrefLang: "es", langRoute: "es" },
    { hrefLang: "pt-BR", langRoute: "br" },
    { hrefLang: "zh-Hans", langRoute: "zh" },
    { hrefLang: "ko", langRoute: "ko" },
    { hrefLang: "ru", langRoute: "ru" },
    { hrefLang: "pl", langRoute: "pl" },
    { hrefLang: "it", langRoute: "it" },
    { hrefLang: "nl", langRoute: "nl" }
];

const UNSUPPORTED_SEARCH_ENGINE_ROUTES = [
    "/ondemand/signup/checkemail",
    "/ondemand/signup/confirmation",
    "/software/confluence/templates/categories/project-management",
    "/software/confluence/templates/kanban-board-whiteboard",
    "/software/confluence/templates/4-ls-retro-whiteboard",
    "/software/confluence/templates/daily-sync-whiteboard",
    "/software/confluence/templates/prioritization-matrix-whiteboard ",
    "/software/confluence/templates/retrospective-whiteboard",
    "/software/confluence/templates/elevator-pitch-whiteboard",
    "/software/confluence/templates/product-roadmap-whiteboard",
    "/software/confluence/templates/disruptive-brainstorm-whiteboard",
    "/software/confluence/templates/incident-postmortem-whiteboard",
    "/software/confluence/templates/sprint-planning-whiteboard",
    "/software/confluence/templates/project-timeline"
];

const smartlingContextTrackerInit = `
    window.SmartlingContextTracker.init({
        orgId: "37bF-QEPm5S9skqlCM0VvQ",
        overrideContextOlderThanDays: 1
    });
`;

const toAssetPath = (name: string) =>
    `${process.env["ASSET_PREFIX"] || ""}/assets/${name}`;

const excludeHardcodedScripts = (src: string) =>
    !src.endsWith("atl-onetrust-wrapper.min.js"); // statically included by PageWrapper

/**
 * if there is a corresponding esmodule defer or sync script in the manifest,
 * treat the given script as a nomodule fallback.
 */
const isNoModuleFallback = (
    script: string,
    manifest: ParcelEntryManifest
): boolean => {
    const basename = script.split(/\..+\.js$/)[0] ?? "";
    if (basename === "") {
        return false;
    }
    return (
        manifest?.esmodule.defer.some((es) => es.startsWith(basename)) ||
        manifest?.esmodule.sync.some((es) => es.startsWith(basename))
    );
};

// eslint-disable-next-line complexity
export const PageWrapper = (props: PageWrapperProps) => {
    const {
        analyticsWebClient,
        location,
        manifest,
        inlineCss,
        pricing,
        availableProducts,
        userProfile,
        userProfileStatus,
        userContext,
        userContextStatus,
        loggedOutContext,
        loggedOutContextStatus,
        countryCode,
        pureClientMode,
        templates,
        agileArticle,
        agileOverview,
        tutorial,
        customerStory,
        partnerTemplates,
        collections,
        categories,
        themeStyles,
        anchorContents,
        pricingContent,
        persona,
        teamType,
        composition,
        isUniformPlayground,
        keyword,
        referrer,
        queryRef,
        mlTemplateIds,
        anchor,
        subAnchor,
        hasCloudSessionTokenCookie
    } = props;
    const intl = useIntl();

    const baseUrl = "https://www.atlassian.com";
    const { locale, path, variant, product } = parseRoute(location);
    const route = path !== "" ? "/" + path : "";

    const localePrefix = locale === "en" ? "" : `/${locale}`;

    const themePrefs = { colorMode: "light" } as const;
    const themeAttrs = getThemeHtmlAttrs(themePrefs);
    const ssrAutoScript = getSSRAutoScript(themePrefs.colorMode);

    const isStagingEnvironment = process.env["NODE_ENV"] === "staging";
    const isInstrumentedLocale = locale === "zz";
    const shouldSendToSmartling = isStagingEnvironment && isInstrumentedLocale;
    const productTitle = product ? `- ${product}` : "";

    useEffect(() => {
        // Theme config for client-only rendering
        setGlobalTheme(themePrefs);
    }, [themePrefs]);

    let title, description;
    switch (route) {
        case "/fastshift/dashboard":
            title = intl.formatMessage({
                id: "FastshiftDashboardPage.title",
                defaultMessage: "Welcome to FastShift"
            });
            description = intl.formatMessage({
                id: "FastshiftDashboardPage.description",
                defaultMessage: "FastShift Dashboard Page"
            });
            break;
        case "/uniform-preview":
            // does not need to be localized
            title = "Uniform Preview";
            description = "Testing Preview page for Uniform";
            break;
        case "/dynamic-content-type":
        case "/test-uniform-3":
        case "/test-uniform-2":
        case "/test-uniform":
            // does not need to be localized
            title = "Test Uniform Page";
            description = "Test Uniform page rendering in WAC";
            break;
        case "/software/rovo/pricing":
            title =
                pricingContent?.pricingTitleExternal ??
                intl.formatMessage({
                    id: "SoftwareRovoPricingPage.title",
                    defaultMessage: "Rovo — Pricing | Atlassian"
                });
            description =
                pricingContent?.pageDescription ??
                intl.formatMessage({
                    id: "SoftwareRovoPricingPage.description",
                    defaultMessage:
                        "Plans and pricing for Rovo, the Generative AI that unlocks enterprise knowledge for teams to make better decisions and reach their goals faster"
                });
            break;
        case "/software/guard/pricing":
            title =
                pricingContent?.pricingTitleExternal ??
                intl.formatMessage({
                    id: "SoftwareGuardPricingPage.title",
                    defaultMessage:
                        "Atlassian Guard Pricing: Secure Solutions for Your Team"
                });
            description =
                pricingContent?.pageDescription ??
                intl.formatMessage({
                    id: "SoftwareGuardPricingPage.description",
                    defaultMessage:
                        "Find the right pricing plan for Atlassian Guard. Secure your team’s data and manage user access effortlessly. Explore pricing options today!"
                });
            break;
        case "/software/compass/pricing":
            title =
                pricingContent?.pricingTitleExternal ??
                intl.formatMessage({
                    id: "SoftwareCompassPricingPage.title",
                    defaultMessage: "Compass - Pricing | Atlassian"
                });
            description =
                pricingContent?.pageDescription ??
                intl.formatMessage({
                    id: "SoftwareCompassPricingPage.description",
                    defaultMessage:
                        "Plans and pricing for Compass, the developer experience platform unifying distributed software architecture and the teams collaborating on it."
                });
            break;
        case "/buy/cloud/org-setup":
            title = intl.formatMessage({
                id: "BuyCloudOrgSetupPage.title",
                defaultMessage: "Atlassian - Cloud Organization Set Up"
            });
            description = intl.formatMessage({
                id: "BuyCloudOrgSetupPage.description",
                defaultMessage: "Atlassian - Cloud Organization Set Up"
            });
            break;
        case "/buy/cloud/site-setup":
            title = intl.formatMessage({
                id: "BuyCloudSiteSetupPage.title",
                defaultMessage: "Direct Buy Site Setup | Atlassian"
            });
            description = intl.formatMessage({
                id: "BuyCloudSiteSetupPage.description",
                defaultMessage: "Direct Buy Site Setup | Atlassian"
            });
            break;
        case "/software/bitbucket/pricing":
            title =
                pricingContent?.pricingTitleExternal ??
                intl.formatMessage({
                    id: "SoftwareBitbucketPricingPage.title",
                    defaultMessage:
                        "Bitbucket Pricing: Find the Right Plan for You | Atlassian"
                });
            description =
                pricingContent?.pageDescription ??
                intl.formatMessage({
                    id: "SoftwareBitbucketPricingPage.description",
                    defaultMessage:
                        "Bitbucket Cloud is free for teams of 5, and Bitbucket Server starts at $10 for ten users. Sign up and choose the right plan to fit your needs."
                });
            break;
        case "/buy/cloud/post-order-provisioning":
            title = intl.formatMessage({
                id: "BuyCloudPostOrderProvisioningPage.title",
                defaultMessage: "Atlassian"
            });
            description = intl.formatMessage({
                id: "BuyCloudPostOrderProvisioningPage.description",
                defaultMessage: ""
            });
            break;
        case "/software/rovo/start":
            title = intl.formatMessage({
                id: "SoftwareRovoStartPage.title",
                defaultMessage: "Get Started with Rovo | Atlassian"
            });
            description = intl.formatMessage({
                id: "SoftwareRovoStartPage.description",
                defaultMessage: "Get Started with Rovo Route"
            });
            break;
        case "/software/jira":
            title = intl.formatMessage({
                id: "JiraSoftwarePage.title",
                defaultMessage:
                    "Jira | Issue & Project Tracking Software | Atlassian"
            });

            if (variant?.length) {
                description = intl.formatMessage({
                    id: "JiraSoftwarePage.paid.description",
                    defaultMessage:
                        "Plan, track, and manage your agile and software development projects in Jira. Customize your workflow, collaborate, and release great software."
                });
            } else {
                description = intl.formatMessage({
                    id: "JiraSoftwarePage.description",
                    defaultMessage:
                        "Make the impossible, possible in Jira. Plan, track, and release world-class software with the number one project management tool for agile teams."
                });
            }
            break;
        case "/software/jira/product-discovery":
            title = intl.formatMessage({
                id: "JiraProductDiscoveryPage.title",
                defaultMessage:
                    "Jira Product Discovery | Prioritization & roadmapping | Atlassian "
            });
            description = intl.formatMessage({
                id: "JiraProductDiscoveryPage.description",
                defaultMessage:
                    "The purpose-built tool to capture ideas, prioritize with insights, and align everyone with roadmaps - all in Jira"
            });
            break;
        case "/software/jira/product-discovery/pricing":
            title =
                pricingContent?.pricingTitleExternal ??
                intl.formatMessage({
                    id: "JiraProductDiscoveryPricingPage.title",
                    defaultMessage:
                        "Jira Product Discovery: Pricing Plans to Fuel Innovation | Atlassian"
                });
            description =
                pricingContent?.pageDescription ??
                intl.formatMessage({
                    id: "JiraProductDiscoveryPricingPage.description",
                    defaultMessage:
                        "Discover the right pricing plan for Jira Product Discovery. Empower your team to innovate and manage ideas effectively. Explore pricing now!"
                });
            break;
        case "/software/jira/pricing":
            title = intl.formatMessage({
                id: "JiraSoftwarePricingPage.title",
                defaultMessage:
                    "Unlock the Best Jira Pricing Plans for Your Team Today | Atlassian"
            });
            description = intl.formatMessage({
                id: "JiraSoftwarePricingPage.description",
                defaultMessage:
                    "The #1 software development tool used by agile teams. Try it free or buy now. Available for cloud or server. Built for teams of all sizes."
            });
            break;
        case "/teams/nonprofits/discount-pricing":
            title = intl.formatMessage({
                id: "NonProfitPricingPage.title",
                defaultMessage:
                    "Nonprofit Discount Eligibility and Pricing | Atlassian"
            });
            description = intl.formatMessage({
                id: "NonProfit.description",
                defaultMessage:
                    "Get discounted Atlassian tools for nonprofit teams"
            });
            break;
        case "/software/jira/service-management":
            title = intl.formatMessage({
                id: "JiraServiceManagementPage.title",
                defaultMessage:
                    "Revolutionize IT Support with Jira Service Management | Atlassian"
            });
            description = intl.formatMessage({
                id: "JiraServiceManagementPage.description",
                defaultMessage:
                    "Tired of slow IT responses? Upgrade to Jira Service Management for faster, efficient service, and exceptional support. Try it today!"
            });

            break;
        case "/software/jira/service-management/pricing":
            title =
                pricingContent?.pricingTitleExternal ??
                intl.formatMessage({
                    id: "JiraServiceManagementSoftwarePricingPage.title",
                    defaultMessage:
                        "Explore Jira Service Management Pricing Plans | Atlassian"
                });
            description =
                pricingContent?.pageDescription ??
                intl.formatMessage({
                    id: "JiraServiceManagementSoftwarePricingPage.description",
                    defaultMessage:
                        "Streamlined IT support at an affordable price. Browse Jira Service Management's tailored pricing plans and find your perfect fit today!"
                });
            break;
        case "/software/confluence":
            title = intl.formatMessage({
                id: "ConfluencePage.title",
                defaultMessage:
                    "Confluence | Your Remote-Friendly Team Workspace | Atlassian"
            });

            if (variant?.length) {
                description = intl.formatMessage({
                    id: "ConfluencePage.paid.description",
                    defaultMessage:
                        "Confluence is where you create, organize and discuss work with your team."
                });
            } else {
                description = intl.formatMessage({
                    id: "ConfluencePage.description",
                    defaultMessage:
                        "Make the impossible, possible with Confluence Cloud. Your team workspace trusted for documentation, project collaboration, Jira integrations, and more!"
                });
            }
            break;
        case "/software/confluence/templates/search":
            title = intl.formatMessage({
                id: "ConfluenceTemplatesSearchPage.title",
                defaultMessage: "Search Results | Atlassian"
            });
            break;

        case "/software/confluence/pricing":
            title = intl.formatMessage({
                id: "ConfluencePricingPage.title",
                defaultMessage:
                    "Confluence Pricing: Free and Paid Plans | Atlassian"
            });
            description = intl.formatMessage({
                id: "ConfluencePricingPage.paid.description",
                defaultMessage:
                    "Find the perfect Confluence plan to ignite collaboration and supercharge productivity within your team. Discover your match today!"
            });

            break;
        case "/software/loom/pricing":
            title =
                pricingContent?.pricingTitleExternal ??
                intl.formatMessage({
                    id: "LoomPricingPage.title",
                    defaultMessage:
                        "Loom Pricing: Free and Paid Plans | Atlassian"
                });
            description =
                pricingContent?.pageDescription ??
                intl.formatMessage({
                    id: "LoomPricingPage.description",
                    defaultMessage:
                        "Explore Loom pricing plans to help every team have fewer, better meetings. Get started for free and record unlimited, AI-enhanced videos to move your work forward today!"
                });
            break;
        case "/jira":
            title = intl.formatMessage({
                id: "JiraLandingPage.title",
                defaultMessage:
                    "Jira Products: Accomplish Great Work as a Team | Atlassian"
            });
            description = intl.formatMessage({
                id: "JiraLandingPage.description",
                defaultMessage:
                    "Jira products are a family of project planning & work management solutions that empower all teams to work with agility and do their best - together."
            });

            break;
        case "/ondemand/signup/confirmation":
            title = intl.formatMessage({
                id: "ConfirmationPage.title",
                defaultMessage: "Confirmation | Atlassian"
            });
            description = undefined;
            break;
        case "/ondemand/signup/checkemail":
            title = intl.formatMessage({
                id: "CheckemailPage.title",
                defaultMessage: "Check Email | Atlassian"
            });
            description = intl.formatMessage({
                id: "CheckemailPage.description",
                defaultMessage:
                    "Millions of users globally rely on Atlassian products every day for improving software development, project management, collaboration, and code quality."
            });
            break;
        case "/try/cloud/signup":
            title = intl.formatMessage(
                {
                    id: "SignupPage.productTitle",
                    defaultMessage:
                        "Sign up {product} - Try Atlassian Cloud | Atlassian"
                },
                { product: productTitle }
            );
            description = intl.formatMessage({
                id: "SignupPage.description",
                defaultMessage: "Try our products in the Cloud, free for 7 days"
            });
            break;
        case "/software/jira/features":
            title = intl.formatMessage({
                id: "JiraSoftwareFeaturesPage.title",
                defaultMessage: "Jira Software - Features | Atlassian"
            });
            description = intl.formatMessage({
                id: "JiraSoftwareFeaturesPage.description",
                defaultMessage:
                    "Jira Software is equipped with features that support every stage of your software development process to help you plan, track and report."
            });
            break;
        case "/try":
            title = intl.formatMessage({
                id: "TryPage.title",
                defaultMessage: "Try Atlassian Products for Free | Atlassian"
            });
            description = intl.formatMessage({
                id: "TryPage.description",
                defaultMessage:
                    "Try Atlassian software development tools for free. See how Jira, Confluence, and our other tools can help your team track projects and collaborate"
            });
            break;
        case "/software/jira/free":
            title = intl.formatMessage({
                id: "JiraSoftwareFreePage.title",
                defaultMessage:
                    "Jira Free: Get Jira Software for Free | Atlassian"
            });
            description = intl.formatMessage({
                id: "JiraSoftwareFreePage.description",
                defaultMessage:
                    "Get Jira for free. The #1 software development tool used by agile teams is free for up to 10 users - get started today."
            });
            break;
        case "/software/confluence/free":
            title = intl.formatMessage({
                id: "ConfluenceFreePage.title",
                defaultMessage:
                    "Confluence Free: Get Confluence for Free | Atlassian"
            });
            description = intl.formatMessage({
                id: "ConfluenceFreePage.description",
                defaultMessage:
                    "Get Confluence for free. Make the impossible, possible with Confluence Cloud. Your team workspace trusted for documentation, project collaboration, Jira integrations, and more!"
            });
            break;
        case "":
            title = intl.formatMessage({
                id: "HomePage.title",
                defaultMessage:
                    "Collaboration software for software, IT and business teams"
            });
            description = intl.formatMessage({
                id: "HomePage.description",
                defaultMessage:
                    "Atlassian's team collaboration software like Jira, Confluence and Trello help teams organize, discuss, and complete shared work."
            });
            break;
        case "/customers":
            title = intl.formatMessage({
                id: "CustomersPage.title",
                defaultMessage:
                    "Atlassian Customers | Achieving Together What's Impossible Alone"
            });
            description = intl.formatMessage({
                id: "CustomersPage.description",
                defaultMessage:
                    "Learn why teams of all sizes prefer Atlassian collaboration software to achieve the impossible through better teamwork."
            });
            break;
        case "/enterprise/contact":
            title = intl.formatMessage({
                id: "EnterpriseContactPage.title",
                defaultMessage:
                    "Contact Atlassian for Enterprise Solutions | Atlassian"
            });
            description = intl.formatMessage({
                id: "EnterpriseContactPage.description",
                defaultMessage:
                    "Contact us to learn more about our enterprise offerings and solutions. Speak to an Atlassian enterprise expert today."
            });
            break;
        case "/enterprise":
            title = intl.formatMessage({
                id: "EnterprisePage.title",
                defaultMessage: "Enterprise | Atlassian"
            });
            description = intl.formatMessage({
                id: "EnterprisePage.description",
                defaultMessage:
                    "Power your organization with our enterprise suite of products to promote team agility, supercharge IT capabilities, and enable teams across your enterprise"
            });
            break;
        case "/search":
            title = intl.formatMessage({
                id: "SearchPage.title",
                defaultMessage: "Search | Atlassian"
            });
            description = intl.formatMessage({
                id: "SearchPage.description",
                defaultMessage:
                    "Search the Atlassian website for product information and resources"
            });
            break;
        case "/collections/strategy/pricing":
            title =
                pricingContent?.pricingTitleExternal ??
                intl.formatMessage({
                    id: "StrategyCollectionPricingPage.title",
                    defaultMessage: "Strategy Collection - Pricing | Atlassian"
                });
            description =
                pricingContent?.pageDescription ??
                intl.formatMessage({
                    id: "StrategyCollectionPricingPage.description",
                    defaultMessage:
                        "The Atlassian Strategy Collection bridges the gaps from strategy to execution. Learn about the collection pricing and features."
                });
            break;
        case "/collections/teamwork/pricing":
            title =
                pricingContent?.pricingTitleExternal ??
                intl.formatMessage({
                    id: "TeamworkCollectionPricingPage.title",
                    defaultMessage:
                        "Teamwork Collection Pricing: Apps and agents to supercharge teamwork | Atlassian"
                });
            description =
                pricingContent?.pageDescription ??
                intl.formatMessage({
                    id: "TeamworkCollectionPricingPage.description",
                    defaultMessage:
                        "The Atlassian Teamwork Collection transforms the way teams collaborate by forming a common language for teamwork across the enterprise. Learn about the collection pricing and features."
                });
            break;
        default:
            title = intl.formatMessage({
                id: "404.title",
                defaultMessage: " | Atlassian"
            });

            // If this page is coming from a Uniform composition:
            // its `Page` definition will include a required page title and optional description
            if (composition?.parameters?.["pageTitle"]?.value) {
                title = composition.parameters["pageTitle"].value;
                description = composition.parameters["metaDescription"]?.value;
                break;
            }

            if (
                /(agile\/(scrum|kanban|project-management|product-management|agile-at-scale|software-development|design|agile-marketing|advantage|teams|tutorials|agile-conversations))$/.test(
                    route
                )
            ) {
                title = intl.formatMessage({
                    id: "AgileOverviewPage.title",
                    defaultMessage:
                        agileOverview?.title || "Agile Overview | Atlassian"
                });
                description = agileOverview?.description;
                break;
            }

            if (route.startsWith("/agile/tutorials/")) {
                title = `${tutorial?.title ?? ""} | Atlassian`;
                description = tutorial?.description;
                break;
            }

            if (route.startsWith("/agile")) {
                title = intl.formatMessage({
                    id: "AgileArticlePage.title",
                    defaultMessage:
                        agileArticle?.title || "Agile Article | Atlassian"
                });
                description = agileArticle?.description;
                break;
            }

            // This route will need to be updated when more Jira templates are included via CPAPI
            if (
                route.startsWith("/software/jira/templates/service-management")
            ) {
                title = intl.formatMessage({
                    id: "jsm.templates.page.pageTitle",
                    defaultMessage:
                        "Service Management Templates | Jira Template Library"
                });
                description = intl.formatMessage({
                    id: "jsm.templates.page.description",
                    defaultMessage:
                        "Empower any team to get started with a service experience in Jira Service Management with pre-configured request types, workflows, and automations."
                });
            }

            if (route.startsWith("/software/confluence/templates")) {
                title = intl.formatMessage({
                    id: "ConfluenceTemplatesPage.title",
                    defaultMessage: "Confluence Templates | Atlassian"
                });
                description = intl.formatMessage({
                    id: "ConfluenceTemplatesPage.description",
                    defaultMessage:
                        "Confluence templates help teams create, organize, and discuss work. Get started with our template library."
                });
                // get the metadata from the template data
                const templatePageMeta = getTemplateMetadata({
                    route,
                    templates: props.templates,
                    collections: props.collections,
                    categories: props.categories
                });
                if (templatePageMeta?.title) {
                    title = templatePageMeta.title;
                    const isSeoUpdate = FeatureGates.checkGate(
                        "gwp-4052-wac-conf-templates-seo"
                    );

                    // Only show the new titles for English during initial SEO team validation
                    if (isSeoUpdate && locale === "en") {
                        switch (templatePageMeta.titleSuffix) {
                            case "template":
                                title = intl.formatMessage(
                                    {
                                        id: "ConfluenceTemplatesDetailPage.pageTitle",
                                        defaultMessage:
                                            "Free {title} template | Confluence"
                                    },
                                    {
                                        title
                                    }
                                );
                                break;
                            case "collection":
                                title = intl.formatMessage(
                                    {
                                        id: "ConfluenceTemplatesCollectionDetailPage.pageTitle",
                                        defaultMessage:
                                            "Free {title} collection | Confluence"
                                    },
                                    {
                                        title
                                    }
                                );
                                break;
                            default:
                                title = intl.formatMessage(
                                    {
                                        id: "ConfluenceTemplatesCategoryDetailPage.pageTitle",
                                        defaultMessage:
                                            "Free {title} templates | Confluence"
                                    },
                                    {
                                        title
                                    }
                                );
                        }
                    } else {
                        switch (templatePageMeta.titleSuffix) {
                            case "template":
                                title +=
                                    " " +
                                    intl.formatMessage({
                                        id: "ConfluenceTemplatesDetailPage.title-suffix",
                                        defaultMessage: "Template | Atlassian"
                                    });
                                break;
                            case "collection":
                                title +=
                                    " " +
                                    intl.formatMessage({
                                        id: "ConfluenceTemplatesCollectionDetailPage.title-suffix",
                                        defaultMessage: "Collection | Atlassian"
                                    });
                                break;
                            default:
                                title += " | Atlassian";
                        }
                    }
                }
                if (templatePageMeta?.description) {
                    description = templatePageMeta.description;
                }
            }
            break;
    }

    if (pureClientMode) {
        return (
            <>
                <style
                    suppressHydrationWarning
                    dangerouslySetInnerHTML={{ __html: inlineCss || "" }}
                />
                <link rel="canonical" href={`${baseUrl}${route}`} />
                <link
                    rel="alternate"
                    href={`${baseUrl}${route}`}
                    hrefLang="x-default"
                />
                {SUPPORTED_LOCALES.map(({ hrefLang, langRoute }) => (
                    <link
                        key={hrefLang}
                        rel="alternate"
                        href={`${baseUrl}/${langRoute}${route}`}
                        hrefLang={hrefLang}
                    />
                ))}
                <Layout
                    analyticsWebClient={analyticsWebClient || null}
                    location={location}
                    pricing={pricing}
                    availableProducts={availableProducts}
                    templates={templates}
                    agileArticle={agileArticle}
                    agileOverview={agileOverview}
                    tutorial={tutorial}
                    customerStory={customerStory}
                    partnerTemplates={partnerTemplates}
                    collections={collections}
                    categories={categories}
                    userProfile={userProfile}
                    userContext={userContext}
                    loggedOutContext={loggedOutContext}
                    countryCode={countryCode}
                    anchorContents={anchorContents}
                    pricingContent={pricingContent}
                    persona={persona || DEFAULT_PERSONA}
                    teamType={teamType}
                    composition={composition}
                    isUniformPlayground={isUniformPlayground}
                    keyword={keyword}
                    mlTemplateIds={mlTemplateIds}
                    queryRef={queryRef}
                    referrer={referrer}
                    anchor={anchor}
                    subAnchor={subAnchor}
                >
                    {props.children}
                </Layout>
            </>
        );
    }

    return (
        <html lang={intl.locale} {...themeAttrs}>
            <head>
                <meta charSet="utf-8" />
                <title>
                    {title !== undefined && title.length > 0
                        ? title.charAt(0).toUpperCase() + title.slice(1)
                        : title}
                </title>
                {description && (
                    <meta name="description" content={description} />
                )}
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1"
                />
                <meta httpEquiv="X-UA-Compatible" content="IE=edge, chrome=1" />
                <meta name="author" content="Atlassian" />
                {UNSUPPORTED_SEARCH_ENGINE_ROUTES.includes(route) && (
                    <meta name="robots" content="noindex,nofollow" />
                )}

                <style
                    suppressHydrationWarning
                    dangerouslySetInnerHTML={{ __html: inlineCss || "" }}
                    id="ssr_style"
                />
                <link
                    rel="canonical"
                    href={`${baseUrl}${localePrefix}${route}`}
                />
                <link
                    rel="alternate"
                    href={`${baseUrl}${route}`}
                    hrefLang="x-default"
                />
                {SUPPORTED_LOCALES.map(({ hrefLang, langRoute }) => (
                    <link
                        key={hrefLang}
                        rel="alternate"
                        href={`${baseUrl}/${langRoute}${route}`}
                        hrefLang={hrefLang}
                    />
                ))}
                {(themeStyles || []).map((theme) => (
                    <link
                        rel="stylesheet"
                        key={theme.id}
                        {...theme.attrs}
                        href={`${
                            process.env["ASSET_PREFIX"] || ""
                        }/assets/atlaskit-tokens_${theme.id}.css`}
                    />
                ))}
                {ssrAutoScript ? (
                    <script
                        dangerouslySetInnerHTML={{ __html: ssrAutoScript }}
                    />
                ) : null}
                <script
                    id="onetrust"
                    type="text/javascript"
                    src={process.env["ONETRUST_SCRIPT_URL"]}
                    data-domain={process.env["ONETRUST_SCRIPT_GUID"]}
                    data-test={process.env["ONETRUST_DATA_TEST"]}
                    data-geofence="false"
                    data-sync="true"
                />
                {shouldSendToSmartling && (
                    <>
                        <script
                            type="text/javascript"
                            src="//d2c7xlmseob604.cloudfront.net/tracker.min.js"
                        />
                        <script
                            dangerouslySetInnerHTML={{
                                __html: smartlingContextTrackerInit
                            }}
                        />
                    </>
                )}

                {manifest?.["global"].sync
                    .filter(excludeHardcodedScripts)
                    .filter((src) => src.endsWith(".js"))
                    .map((src, i) => (
                        <script key={i} src={toAssetPath(src)} />
                    ))}

                {manifest?.["global"].defer
                    .filter(excludeHardcodedScripts)
                    .filter((src) => src.endsWith(".js"))
                    .map((src, i) =>
                        isNoModuleFallback(src, manifest) ? (
                            <script
                                key={i}
                                defer
                                noModule
                                src={toAssetPath(src)}
                            />
                        ) : (
                            <script key={i} defer src={toAssetPath(src)} />
                        )
                    )}

                {manifest?.["global"].async
                    .filter(excludeHardcodedScripts)
                    .filter((src) => src.endsWith(".js"))
                    .map((src, i) => (
                        <script key={i} async src={toAssetPath(src)} />
                    ))}

                {manifest?.esmodule.sync
                    .filter(excludeHardcodedScripts)
                    .filter((src) => src.endsWith(".js"))
                    .map((src, i) => (
                        <script key={i} type="module" src={toAssetPath(src)} />
                    ))}

                {manifest?.esmodule.defer
                    .filter(excludeHardcodedScripts)
                    .filter((src) => src.endsWith(".js"))
                    .map((src, i) => (
                        <script key={i} type="module" src={toAssetPath(src)} />
                    ))}

                {manifest?.esmodule.async
                    .filter(excludeHardcodedScripts)
                    .filter((src) => src.endsWith(".js"))
                    .map((src, i) => (
                        <script
                            key={i}
                            async
                            type="module"
                            src={toAssetPath(src)}
                        />
                    ))}
            </head>
            <body>
                <div id="wac-root">
                    <Layout
                        analyticsWebClient={analyticsWebClient || null}
                        location={location}
                        pricing={pricing}
                        availableProducts={availableProducts}
                        templates={templates}
                        agileArticle={agileArticle}
                        agileOverview={agileOverview}
                        tutorial={tutorial}
                        customerStory={customerStory}
                        partnerTemplates={partnerTemplates}
                        collections={collections}
                        categories={categories}
                        userProfile={userProfile}
                        userProfileStatus={userProfileStatus}
                        userContext={userContext}
                        userContextStatus={userContextStatus}
                        loggedOutContext={loggedOutContext}
                        loggedOutContextStatus={loggedOutContextStatus}
                        countryCode={countryCode}
                        anchorContents={anchorContents}
                        pricingContent={pricingContent}
                        persona={persona}
                        composition={composition}
                        isUniformPlayground={isUniformPlayground}
                        keyword={keyword}
                        mlTemplateIds={mlTemplateIds}
                        referrer={referrer}
                        queryRef={queryRef}
                        hasCloudSessionTokenCookie={hasCloudSessionTokenCookie}
                        anchor={anchor}
                        subAnchor={subAnchor}
                    >
                        {props.children}
                    </Layout>
                </div>
            </body>
        </html>
    );
};
